/**
 * @fileoverview Mobile Detection Hook
 *
 * Provides responsive breakpoint detection for mobile vs desktop layouts.
 * Uses matchMedia for efficient, event-driven updates.
 *
 * @module hooks/use-mobile
 */

import * as React from "react";

/**
 * Breakpoint width in pixels.
 * Below this width is considered "mobile".
 * Matches Tailwind's `md` breakpoint.
 */
const MOBILE_BREAKPOINT = 768;

/**
 * Hook to detect if viewport is mobile-sized.
 *
 * Uses `matchMedia` API for efficient resize detection without
 * attaching window resize listeners.
 *
 * @returns {boolean} True if viewport width < 768px
 *
 * @example
 * const isMobile = useIsMobile();
 *
 * return isMobile ? <MobileNav /> : <DesktopNav />;
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    // WHY: Use matchMedia for performance - only fires on breakpoint changes
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    // Set initial value
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // WHY: Double negation converts undefined to false on first render
  return !!isMobile;
}
